import { LookupRecords } from "../config/viewConditionOperators";

// Held for the session, as advanced search holds its lookup lists. Shared by the
// designer and the live pages, so each entity's records are read once.
export const lookupRecordsCache: LookupRecords = {};

/** Only for tests - the cache is otherwise session-lifetime by design. */
export function clearLookupRecordsCache() {
  Object.keys(lookupRecordsCache).forEach((key) => delete lookupRecordsCache[key]);
}

// The endpoint sends a whole batch to the warehouse if any name in it is a warehouse
// entity, so a mixed batch is split by backend.
const isWarehouseEntity = (name: string) => name.toLowerCase().startsWith("dw");

/** Asks for the communication audiences with the type and run-at each was made for, which a communication matches against. */
export const AudiencesWithTypes = "withtypes";

async function readAudiencesWithTypes(dispatch: unknown): Promise<LookupRecords> {
  const { Get, isFailedResponse } = await import("../api/request");
  const response = await Get(dispatch, "GetCommunicationAudiences");

  if (isFailedResponse(response)) {
    throw new Error("Lookup records could not be read");
  }

  return {
    g4c_communicationaudience: (Array.isArray(response?.data) ? response.data : []).map(
      (audience: { Id: string; Name: string; RunAt: string; Type: number }) => ({
        Key: String(audience.Id),
        Value: audience.Name,
        RunAt: audience.RunAt,
        Type: audience.Type,
      })
    ),
  };
}

async function readBatch(
  dispatch: unknown,
  names: string[],
  searchFilter = ""
): Promise<LookupRecords> {
  if (searchFilter === AudiencesWithTypes && names.length === 1 && names[0] === "g4c_communicationaudience") {
    return readAudiencesWithTypes(dispatch);
  }

  // Loaded on first read, so rendering a field doesn't pull the service into every
  // page-component test.
  const { GetMultipleEntityBasicInfo } = await import("../api/entityService");
  const { isFailedResponse } = await import("../api/request");

  return GetMultipleEntityBasicInfo(
    dispatch,
    names.map((name) => ({ entityName: name, searchFilter }))
  ).then((response: { data?: unknown }) => {
    // Rejected, so a failed read is not held for the session as "no records".
    if (isFailedResponse(response)) {
      throw new Error("Lookup records could not be read");
    }

    return Object.fromEntries(
      (Array.isArray(response?.data) ? response.data : []).map(
        (result: {
          EntityName?: string;
          Results?: ({ Key: string; Value: string } & Record<string, unknown>)[];
        }) => [
          (result.EntityName ?? "").toLowerCase(),
          // Extra fields ride along, such as a fixture's OpponentId, for option filters.
          (result.Results ?? []).map((record) => ({
            ...record,
            Key: String(record.Key),
            Value: record.Value,
          })),
        ]
      )
    );
  });
}

/** The records lookups to each entity can point at, one list per entity. */
export async function loadLookupRecords(
  dispatch: unknown,
  names: string[]
): Promise<LookupRecords> {
  const warehouse = names.filter(isWarehouseEntity);
  const ticketing = names.filter((name) => !isWarehouseEntity(name));

  const batches = await Promise.all(
    [warehouse, ticketing]
      .filter((batch) => batch.length > 0)
      .map((batch) => readBatch(dispatch, batch))
  );

  return Object.assign({}, ...batches);
}

/**
 * One entity's records narrowed by a named filter the service knows, e.g. "contactflow".
 * Keyed by the entity, as `loadLookupRecords` is.
 */
export function loadFilteredLookupRecords(
  dispatch: unknown,
  name: string,
  searchFilter: string
): Promise<LookupRecords> {
  return readBatch(dispatch, [name], searchFilter);
}
